import { computed, ref, unref } from "vue";
import { useI18n } from "vue-i18n";
import { CUSTOMER_NAME_FACET_NAME } from "@/core/constants";
import { DateFilterId, XApiPermissions } from "@/core/enums";
import {
  escapeFilterSyntaxValue,
  toEndDateFilterValue,
  toStartDateFilterValue,
  toDateISOString,
  toLocalDateOnly,
} from "@/core/utilities";
import { useUser } from "./useUser";
import { facets } from "./useUserOrders";
import type { DateFilterType } from "@/core/types";
import type { OrdersFilterDataType, OrdersFilterChipsItemType, OrderFacetType, OrderScopeType } from "@/shared/account";
import type { MaybeRef, Ref } from "vue";

export function getFilterExpression(keyword: string, filterData: OrdersFilterDataType): string {
  let filterExpression = "";
  if (keyword) {
    filterExpression += `${keyword} `;
  }
  if (filterData.statuses.length) {
    const statuses = filterData.statuses.map((status) => `"${escapeFilterSyntaxValue(status)}"`);
    filterExpression += `status:${statuses.join(",")} `;
  }
  if (filterData.customerNames?.length) {
    const customerNames = filterData.customerNames.map((name) => `"${escapeFilterSyntaxValue(name)}"`);
    filterExpression += `customername:${customerNames.join(",")} `;
  }

  const startDateFilterValue = toStartDateFilterValue(filterData.startDate);
  const endDateFilterValue = toEndDateFilterValue(filterData.endDate);
  if (startDateFilterValue || endDateFilterValue) {
    let createdDateFilterValue = "";
    if (startDateFilterValue) {
      createdDateFilterValue += `"${startDateFilterValue}" `;
    }
    createdDateFilterValue += "TO";
    if (endDateFilterValue) {
      createdDateFilterValue += ` "${endDateFilterValue}"`;
    }
    filterExpression += `createddate:[${createdDateFilterValue}]`;
  }

  filterExpression = filterExpression.trim();

  return filterExpression;
}

const filterData: Ref<OrdersFilterDataType> = ref({ statuses: [], customerNames: [] });
const appliedFilterData: Ref<OrdersFilterDataType> = ref({ ...filterData.value });
const facetLocalization: Ref<OrderFacetType[] | undefined> = ref();
const selectedDateFilterType: Ref<DateFilterType | undefined> = ref();

function getFirstDayOfWeek(currentDate: Date): Date {
  const date = new Date(currentDate);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);

  return new Date(date.setDate(diff));
}

function applyFilters() {
  if (JSON.stringify(appliedFilterData.value) === JSON.stringify(filterData.value)) {
    return;
  }
  appliedFilterData.value = { ...filterData.value };
}

function resetFilterData() {
  filterData.value = { statuses: [], customerNames: [], startDate: undefined, endDate: undefined };
  appliedFilterData.value = { ...filterData.value };
}

function setFacetsLocalization(localizationFacets: OrderFacetType[] | undefined) {
  facetLocalization.value = localizationFacets;
}

function getFacetLocalization(facetName: string, term: string): string | undefined {
  const facet = facetLocalization.value?.find((el) => el.name === facetName);
  return facet?.items?.find((el) => el.term === term)?.label;
}

function handleOrdersDateFilterChange(dateFilterType: DateFilterType): void {
  filterData.value.startDate = dateFilterType.startDate ? toDateISOString(dateFilterType.startDate) : undefined;
  filterData.value.endDate = dateFilterType.endDate ? toDateISOString(dateFilterType.endDate) : undefined;
  selectedDateFilterType.value = dateFilterType;
}

export function useUserOrdersFilter(orderScope?: MaybeRef<OrderScopeType>) {
  const { d, t } = useI18n();
  const { checkPermissions } = useUser();

  function getDateFilterRanges(): DateFilterType[] {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    const lastDayStartDate = new Date(currentDate);
    lastDayStartDate.setDate(currentDate.getDate() - 1);

    const firstDayOfWeek = getFirstDayOfWeek(currentDate);

    const lastWeekStartDate = new Date(firstDayOfWeek);
    lastWeekStartDate.setDate(firstDayOfWeek.getDate() - 7);

    const lastWeekEndDate = new Date(firstDayOfWeek);

    const lastMonthStartDate = new Date(currentDate);
    lastMonthStartDate.setDate(1);
    lastMonthStartDate.setMonth(currentDate.getMonth() - 1);

    const lastMonthEndDate = new Date(currentDate);
    lastMonthEndDate.setDate(1);

    const lastYearStartDate = new Date(currentDate);
    lastYearStartDate.setFullYear(currentDate.getFullYear() - 1);
    lastYearStartDate.setMonth(0);
    lastYearStartDate.setDate(1);

    const lastYearEndDate = new Date(currentDate);
    lastYearEndDate.setFullYear(currentDate.getFullYear());
    lastYearEndDate.setMonth(0);
    lastYearEndDate.setDate(1);

    return [
      {
        id: DateFilterId.CUSTOM,
        label: t("common.labels.custom_date"),
      },
      {
        id: DateFilterId.LAST_DAY,
        label: t("common.labels.last_day"),
        startDate: toLocalDateOnly(lastDayStartDate),
        endDate: toLocalDateOnly(currentDate),
      },
      {
        id: DateFilterId.LAST_WEEK,
        label: t("common.labels.last_week"),
        startDate: toLocalDateOnly(lastWeekStartDate),
        endDate: toLocalDateOnly(lastWeekEndDate),
      },
      {
        id: DateFilterId.LAST_MONTH,
        label: t("common.labels.last_month"),
        startDate: toLocalDateOnly(lastMonthStartDate),
        endDate: toLocalDateOnly(lastMonthEndDate),
      },
      {
        id: DateFilterId.LAST_YEAR,
        label: t("common.labels.last_year"),
        startDate: toLocalDateOnly(lastYearStartDate),
        endDate: toLocalDateOnly(lastYearEndDate),
      },
    ];
  }

  const dateFilterTypes = computed(() => getDateFilterRanges());

  const isFilterEmpty = computed(() => {
    const { statuses, startDate, endDate, customerNames } = appliedFilterData.value;
    return !statuses.length && !startDate && !endDate && !customerNames?.length;
  });

  const isFilterDirty = computed(() => {
    return JSON.stringify(filterData.value) !== JSON.stringify(appliedFilterData.value);
  });

  const filterChipsItems = computed(() => {
    const items: OrdersFilterChipsItemType[] = [];

    if (appliedFilterData.value.statuses.length) {
      for (const status of appliedFilterData.value.statuses) {
        items.push({ fieldName: "statuses", value: status, label: getFacetLocalization("status", status) ?? status });
      }
    }

    if (appliedFilterData.value.customerNames?.length) {
      for (const customerName of appliedFilterData.value.customerNames) {
        items.push({ fieldName: "customerNames", value: customerName, label: customerName });
      }
    }

    const startDateFilterValue = toStartDateFilterValue(appliedFilterData.value.startDate);
    const endDateFilterValue = toEndDateFilterValue(appliedFilterData.value.endDate);
    if (startDateFilterValue) {
      const formattedDate = d(startDateFilterValue);
      items.push({
        fieldName: "startDate",
        value: appliedFilterData.value.startDate,
        label: t("common.labels.starts_from", [formattedDate]),
      });
    }
    if (endDateFilterValue) {
      const formattedDate = d(endDateFilterValue);
      items.push({
        fieldName: "endDate",
        value: appliedFilterData.value.endDate,
        label: t("common.labels.ends_to", [formattedDate]),
      });
    }
    return items;
  });

  const organizationCustomerNames = computed(() =>
    facets.value?.find((item) => item.name === CUSTOMER_NAME_FACET_NAME)?.items?.map((item) => item.label),
  );

  const showCustomerNameFilter = computed(() => {
    const scope = orderScope ? unref(orderScope) : undefined;
    return (
      checkPermissions(XApiPermissions.CanViewOrganizationOrders) &&
      scope === "organization" &&
      !!organizationCustomerNames.value?.length
    );
  });

  function resetFilters() {
    resetFilterData();
    selectedDateFilterType.value = dateFilterTypes.value[0];
    selectedDateFilterType.value.startDate = undefined;
    selectedDateFilterType.value.endDate = undefined;
  }

  function removeFilterChipsItem(item: OrdersFilterChipsItemType) {
    if (item.fieldName === "statuses") {
      appliedFilterData.value.statuses.splice(appliedFilterData.value.statuses.indexOf(item.value as string), 1);
    }

    if (item.fieldName === "customerNames") {
      appliedFilterData.value.customerNames?.splice(
        appliedFilterData.value.customerNames.indexOf(item.value as string),
        1,
      );
    }

    if (item.fieldName === "startDate") {
      appliedFilterData.value.startDate = undefined;
    }

    if (item.fieldName === "endDate") {
      appliedFilterData.value.endDate = undefined;
    }

    filterData.value = { ...appliedFilterData.value };

    if (item.fieldName === "startDate" || item.fieldName === "endDate") {
      selectedDateFilterType.value = dateFilterTypes.value[0];
      selectedDateFilterType.value.startDate = appliedFilterData.value?.startDate;
      selectedDateFilterType.value.endDate = appliedFilterData.value?.endDate;
    }
  }

  return {
    filterData,
    appliedFilterData: computed(() => appliedFilterData.value),
    dateFilterTypes,
    isFilterEmpty,
    isFilterDirty,
    filterChipsItems,
    organizationCustomerNames,
    showCustomerNameFilter,
    applyFilters,
    resetFilters,
    resetFilterData,
    removeFilterChipsItem,
    setFacetsLocalization,
    selectedDateFilterType,
    handleOrdersDateFilterChange,
  };
}
